/**
 * PostHog Session Cache
 *
 * Stores distinct_id and session_id after PostHog's `loaded` callback fires.
 * Allows getTelemetryHeaders() to return correct values even if called before
 * PostHog's live get_*() methods are ready (e.g. during rapid first analysis).
 *
 * Lifecycle:
 *   initPostHog() → PostHog snippet injects array.js (async)
 *   → loaded callback fires → setPostHogCache() called
 *   → onSessionId fires when recording starts → updateSessionId() called
 *   → analyzeMessage() → getTelemetryHeaders() reads from cache
 *
 * Storage: Uses window.__posthogCache (not module-level variables) so that
 * webpack HMR re-evaluation of this module does not clear cached values.
 * Module-level `let` variables reset to null on HMR, but window properties
 * survive across hot reloads.
 */

declare global {
  interface Window {
    __posthogCache?: { distinctId: string | null; sessionId: string | null };
  }
}

function getStore(): { distinctId: string | null; sessionId: string | null } {
  if (typeof window !== 'undefined') {
    if (!window.__posthogCache) {
      window.__posthogCache = { distinctId: null, sessionId: null };
    }
    return window.__posthogCache;
  }
  // SSR / non-browser fallback — ephemeral, module-level
  return _ssrCache;
}

// SSR-only fallback (never hit in browser)
const _ssrCache: { distinctId: string | null; sessionId: string | null } = {
  distinctId: null,
  sessionId: null,
};

export function setPostHogCache(distinctId: string | null, sessionId: string | null): void {
  const store = getStore();
  if (distinctId) store.distinctId = distinctId;
  if (sessionId) store.sessionId = sessionId;
}

export function updateSessionId(sessionId: string): void {
  if (sessionId) getStore().sessionId = sessionId;
}

export function getPostHogCache(): { distinctId: string | null; sessionId: string | null } {
  const store = getStore();
  return { distinctId: store.distinctId, sessionId: store.sessionId };
}
